import { supabase } from '../supabase';
import { 
  CrmConnectionConfig, 
  FieldMapping, 
  ConnectionStatus,
  CrmErrorType,
  OAuth2Config,
  ApiKeyConfig,
  BasicAuthConfig,
  CustomAuthConfig
} from './types';
import { encryptData, decryptData } from './encryption';

/**
 * Fetch all CRM connections for the current user
 */
export async function fetchCrmConnections(): Promise<CrmConnectionConfig[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('user_credentials')
      .select('*')
      .eq('user_id', user.id)
      .like('provider', '%_crm');
    
    if (error) throw error;
    
    // Transform and decrypt sensitive data
    return data.map(item => {
      const provider = item.provider.replace('_crm', '');
      const credentials = item.credentials;
      
      // Base connection config
      const config: Partial<CrmConnectionConfig> = {
        id: item.id,
        name: credentials.name || `${provider.charAt(0).toUpperCase() + provider.slice(1)} CRM`,
        provider,
        url: credentials.url || '',
        authMethod: credentials.authMethod || 'oauth2',
        enabled: credentials.enabled !== false,
        timeout: credentials.timeout,
        retryAttempts: credentials.retryAttempts,
        retryDelay: credentials.retryDelay,
        headers: credentials.headers,
        parameters: credentials.parameters,
        createdAt: item.created_at,
        updatedAt: item.updated_at
      };
      
      // Add auth method specific properties
      switch (config.authMethod) {
        case 'oauth2':
          return {
            ...config,
            authMethod: 'oauth2',
            clientId: credentials.clientId,
            clientSecret: credentials.clientSecret ? decryptData(credentials.clientSecret) : undefined,
            authorizationUrl: credentials.authorizationUrl,
            tokenUrl: credentials.tokenUrl,
            redirectUri: credentials.redirectUri,
            scope: credentials.scope,
            accessToken: credentials.accessToken ? decryptData(credentials.accessToken) : undefined,
            refreshToken: credentials.refreshToken ? decryptData(credentials.refreshToken) : undefined,
            tokenExpiry: credentials.tokenExpiry
          } as OAuth2Config;
          
        case 'apikey':
          return {
            ...config,
            authMethod: 'apikey',
            apiKey: credentials.apiKey ? decryptData(credentials.apiKey) : undefined,
            apiKeyHeader: credentials.apiKeyHeader,
            apiKeyQueryParam: credentials.apiKeyQueryParam
          } as ApiKeyConfig;
          
        case 'basic':
          return {
            ...config,
            authMethod: 'basic',
            username: credentials.username,
            password: credentials.password ? decryptData(credentials.password) : undefined
          } as BasicAuthConfig;
          
        case 'custom':
          return {
            ...config,
            authMethod: 'custom',
            customAuthData: credentials.customAuthData
          } as CustomAuthConfig;
          
        default:
          return config as CrmConnectionConfig;
      }
    });
  } catch (error) {
    console.error('Error fetching CRM connections:', error);
    throw error;
  }
}

/**
 * Fetch a specific CRM connection by ID
 */
export async function fetchCrmConnection(connectionId: string): Promise<CrmConnectionConfig> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('user_credentials')
      .select('*')
      .eq('id', connectionId)
      .eq('user_id', user.id)
      .single();
    
    if (error) throw error;
    
    // Transform and decrypt sensitive data
    const provider = data.provider.replace('_crm', '');
    const credentials = data.credentials;
    
    // Base connection config
    const config: Partial<CrmConnectionConfig> = {
      id: data.id,
      name: credentials.name || `${provider.charAt(0).toUpperCase() + provider.slice(1)} CRM`,
      provider,
      url: credentials.url || '',
      authMethod: credentials.authMethod || 'oauth2',
      enabled: credentials.enabled !== false,
      timeout: credentials.timeout,
      retryAttempts: credentials.retryAttempts,
      retryDelay: credentials.retryDelay,
      headers: credentials.headers,
      parameters: credentials.parameters,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
    
    // Add auth method specific properties
    switch (config.authMethod) {
      case 'oauth2':
        return {
          ...config,
          authMethod: 'oauth2',
          clientId: credentials.clientId,
          clientSecret: credentials.clientSecret ? decryptData(credentials.clientSecret) : undefined,
          authorizationUrl: credentials.authorizationUrl,
          tokenUrl: credentials.tokenUrl,
          redirectUri: credentials.redirectUri,
          scope: credentials.scope,
          accessToken: credentials.accessToken ? decryptData(credentials.accessToken) : undefined,
          refreshToken: credentials.refreshToken ? decryptData(credentials.refreshToken) : undefined,
          tokenExpiry: credentials.tokenExpiry
        } as OAuth2Config;
        
      case 'apikey':
        return {
          ...config,
          authMethod: 'apikey',
          apiKey: credentials.apiKey ? decryptData(credentials.apiKey) : undefined,
          apiKeyHeader: credentials.apiKeyHeader,
          apiKeyQueryParam: credentials.apiKeyQueryParam
        } as ApiKeyConfig;
        
      case 'basic':
        return {
          ...config,
          authMethod: 'basic',
          username: credentials.username,
          password: credentials.password ? decryptData(credentials.password) : undefined
        } as BasicAuthConfig;
        
      case 'custom':
        return {
          ...config,
          authMethod: 'custom',
          customAuthData: credentials.customAuthData
        } as CustomAuthConfig;
        
      default:
        return config as CrmConnectionConfig;
    }
  } catch (error) {
    console.error(`Error fetching CRM connection ${connectionId}:`, error);
    throw error;
  }
}

/**
 * Save a CRM connection
 */
export async function saveCrmConnection(config: CrmConnectionConfig): Promise<CrmConnectionConfig> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Prepare credentials object, encrypting sensitive data
    const credentials: Record<string, any> = {
      name: config.name,
      url: config.url,
      authMethod: config.authMethod,
      enabled: config.enabled,
      timeout: config.timeout,
      retryAttempts: config.retryAttempts,
      retryDelay: config.retryDelay,
      headers: config.headers,
      parameters: config.parameters
    };
    
    // Add auth method specific properties
    switch (config.authMethod) {
      case 'oauth2':
        const oauth2Config = config as OAuth2Config;
        credentials.clientId = oauth2Config.clientId;
        credentials.clientSecret = oauth2Config.clientSecret ? encryptData(oauth2Config.clientSecret) : undefined;
        credentials.authorizationUrl = oauth2Config.authorizationUrl;
        credentials.tokenUrl = oauth2Config.tokenUrl;
        credentials.redirectUri = oauth2Config.redirectUri;
        credentials.scope = oauth2Config.scope;
        credentials.accessToken = oauth2Config.accessToken ? encryptData(oauth2Config.accessToken) : undefined;
        credentials.refreshToken = oauth2Config.refreshToken ? encryptData(oauth2Config.refreshToken) : undefined;
        credentials.tokenExpiry = oauth2Config.tokenExpiry;
        break;
        
      case 'apikey':
        const apiKeyConfig = config as ApiKeyConfig;
        credentials.apiKey = apiKeyConfig.apiKey ? encryptData(apiKeyConfig.apiKey) : undefined;
        credentials.apiKeyHeader = apiKeyConfig.apiKeyHeader;
        credentials.apiKeyQueryParam = apiKeyConfig.apiKeyQueryParam;
        break;
        
      case 'basic':
        const basicAuthConfig = config as BasicAuthConfig;
        credentials.username = basicAuthConfig.username;
        credentials.password = basicAuthConfig.password ? encryptData(basicAuthConfig.password) : undefined;
        break;
        
      case 'custom':
        const customAuthConfig = config as CustomAuthConfig;
        credentials.customAuthData = customAuthConfig.customAuthData;
        break;
    }
    
    // Save to database
    const { data, error } = await supabase
      .from('user_credentials')
      .upsert({
        id: config.id,
        user_id: user.id,
        provider: `${config.provider}_crm`,
        credentials
      })
      .select()
      .single();
    
    if (error) throw error;
    
    // Return the saved config
    return await fetchCrmConnection(data.id);
  } catch (error) {
    console.error('Error saving CRM connection:', error);
    throw error;
  }
}

/**
 * Delete a CRM connection
 */
export async function deleteCrmConnection(connectionId: string): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('user_credentials')
      .delete()
      .eq('id', connectionId)
      .eq('user_id', user.id);
    
    if (error) throw error;
  } catch (error) {
    console.error(`Error deleting CRM connection ${connectionId}:`, error);
    throw error;
  }
}

/**
 * Test a CRM connection
 */
export async function testCrmConnection(config: CrmConnectionConfig): Promise<ConnectionStatus> {
  try {
    // In a real implementation, this would make an API call to the CRM provider
    // For this demo, we'll simulate a successful connection
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Randomly succeed or fail for demo purposes
    const success = Math.random() > 0.2;
    
    if (!success) {
      // Simulate a random error
      const errorTypes = [
        CrmErrorType.CONNECTION_FAILED,
        CrmErrorType.AUTHENTICATION_FAILED,
        CrmErrorType.RATE_LIMITED,
        CrmErrorType.TIMEOUT
      ];
      const errorType = errorTypes[Math.floor(Math.random() * errorTypes.length)];
      
      const errorMessages = {
        [CrmErrorType.CONNECTION_FAILED]: "Could not connect to the CRM provider. Please check your URL and network connection.",
        [CrmErrorType.AUTHENTICATION_FAILED]: "Authentication failed. Please check your credentials.",
        [CrmErrorType.RATE_LIMITED]: "Rate limit exceeded. Please try again later.",
        [CrmErrorType.TIMEOUT]: "Connection timed out. Please check your network connection or increase the timeout setting."
      };
      
      throw {
        type: errorType,
        message: errorMessages[errorType],
        timestamp: new Date().toISOString(),
        details: { config }
      };
    }
    
    // Return successful connection status
    return {
      id: crypto.randomUUID(),
      connectionId: config.id,
      status: 'connected',
      lastSyncTime: new Date().toISOString(),
      metrics: {
        successfulSyncs: Math.floor(Math.random() * 100),
        failedSyncs: Math.floor(Math.random() * 10),
        totalRecordsSynced: Math.floor(Math.random() * 1000),
        averageSyncTime: Math.floor(Math.random() * 5000)
      }
    };
  } catch (error) {
    console.error('Error testing CRM connection:', error);
    
    // Return error status
    return {
      id: crypto.randomUUID(),
      connectionId: config.id,
      status: 'error',
      lastError: {
        type: error.type || CrmErrorType.UNKNOWN,
        message: error.message || 'An unknown error occurred',
        timestamp: error.timestamp || new Date().toISOString(),
        details: error.details
      }
    };
  }
}

/**
 * Fetch field mappings for a connection
 */
export async function fetchFieldMappings(connectionId: string): Promise<FieldMapping[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('crm_field_mappings')
      .select('*')
      .eq('connection_id', connectionId);
    
    if (error) throw error;
    
    return data.map(item => ({
      id: item.id,
      connectionId: item.connection_id,
      sourceField: item.source_field,
      targetField: item.target_field,
      direction: item.direction,
      transformation: item.transformation,
      enabled: item.enabled
    }));
  } catch (error) {
    console.error(`Error fetching field mappings for connection ${connectionId}:`, error);
    throw error;
  }
}

/**
 * Save field mappings for a connection
 */
export async function saveFieldMappings(connectionId: string, mappings: Omit<FieldMapping, 'id' | 'connectionId'>[]): Promise<FieldMapping[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // First delete existing mappings
    const { error: deleteError } = await supabase
      .from('crm_field_mappings')
      .delete()
      .eq('connection_id', connectionId);
    
    if (deleteError) throw deleteError;
    
    // Then insert new mappings
    const { data, error } = await supabase
      .from('crm_field_mappings')
      .insert(
        mappings.map(mapping => ({
          connection_id: connectionId,
          source_field: mapping.sourceField,
          target_field: mapping.targetField,
          direction: mapping.direction,
          transformation: mapping.transformation,
          enabled: mapping.enabled
        }))
      )
      .select();
    
    if (error) throw error;
    
    return data.map(item => ({
      id: item.id,
      connectionId: item.connection_id,
      sourceField: item.source_field,
      targetField: item.target_field,
      direction: item.direction,
      transformation: item.transformation,
      enabled: item.enabled
    }));
  } catch (error) {
    console.error(`Error saving field mappings for connection ${connectionId}:`, error);
    throw error;
  }
}

/**
 * Fetch connection status
 */
export async function fetchConnectionStatus(connectionId: string): Promise<ConnectionStatus> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('crm_connection_status')
      .select('*')
      .eq('connection_id', connectionId)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        // No status found, return disconnected status
        return {
          id: crypto.randomUUID(),
          connectionId,
          status: 'disconnected'
        };
      }
      throw error;
    }
    
    return {
      id: data.id,
      connectionId: data.connection_id,
      status: data.status,
      lastSyncTime: data.last_sync_time,
      lastError: data.last_error,
      metrics: data.metrics
    };
  } catch (error) {
    console.error(`Error fetching connection status for ${connectionId}:`, error);
    throw error;
  }
}

/**
 * Log connection error
 */
export async function logConnectionError(
  connectionId: string, 
  errorType: CrmErrorType, 
  errorMessage: string, 
  details?: any
): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const errorData = {
      type: errorType,
      message: errorMessage,
      timestamp: new Date().toISOString(),
      details
    };

    // Update connection status
    const { error } = await supabase
      .from('crm_connection_status')
      .upsert({
        connection_id: connectionId,
        status: 'error',
        last_error: errorData,
        updated_at: new Date().toISOString()
      });
    
    if (error) throw error;
    
    // Log error to error log table
    await supabase
      .from('crm_error_logs')
      .insert({
        connection_id: connectionId,
        error_type: errorType,
        error_message: errorMessage,
        error_details: details,
        created_at: new Date().toISOString()
      });
  } catch (error) {
    console.error(`Error logging connection error for ${connectionId}:`, error);
    // Don't throw here to prevent cascading errors
  }
}